import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { noop } from 'ui-components/default-props/noop';

import { PHONE_VERIFICATION_STATES } from '../../authentication/constants';
import { useStepUpAuthContext } from '../../global/step-up-authentication';
import { MANAGEMENT_TYPES } from '../../global/step-up-authentication/constants';
import { ChallengePhaseModalContent } from '../../global/step-up-authentication/phases/challenge';
import { useMfaEnrollment } from '../../hooks/use-mfa-enrollment';
import {
  AuthenticatorEnrollmentForm,
  EmailEnrollmentForm,
  PhoneEnrollmentForm,
} from '../../pages/settings/sign-in-and-security/sections/security/add-mfa-enrollment/enrollment-forms';
import { SelectEnrollmentType } from '../../pages/settings/sign-in-and-security/sections/security/add-mfa-enrollment/select-enrollment-type';
import { messages } from '../../pages/settings/sign-in-and-security/sections/security/messages';
import ModalPortal from '../modal-portal';

const EnrollmentForms = {
  [MANAGEMENT_TYPES.TOTP]: AuthenticatorEnrollmentForm,
  [MANAGEMENT_TYPES.EMAIL]: EmailEnrollmentForm,
  [MANAGEMENT_TYPES.PHONE]: PhoneEnrollmentForm,
};

/**
 * @typedef {import('react-intl').MessageDescriptor} MessageDescriptor
 * @typedef {keyof typeof MANAGEMENT_TYPES} ManagementType
 * @typedef {keyof typeof PHONE_VERIFICATION_STATES} PhoneVerificationState
 */

/**
 * Shared MFA enrollment modal component
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {() => void} props.onClose - Called when modal should close
 * @param {() => void} props.onSuccess - Called when enrollment succeeds
 * @param {ManagementType|null} props.restrictToMfaType - Restrict to specific MFA type or null for all
 * @param {boolean} props.showTypeSelection - Whether to show the type selection phase
 * @param {MessageDescriptor} props.modalTitle - Message descriptor for modal title
 * @param {boolean} props.isUnskippable - Whether modal is in unskippable state
 * @param {boolean} props.allowMfaTypeSwitching - Whether MFA type switching is allowed
 * @param {ManagementType|null} props.initialMfaType - Initial MFA type for switching
 * @param {PhoneVerificationState} [props.phoneVerificationState] - Current phone verification state
 * @returns {JSX.Element|null}
 */
export const MfaEnrollmentModal = ({
  isOpen,
  onClose,
  onSuccess,
  restrictToMfaType = null,
  showTypeSelection = true,
  modalTitle,
  isUnskippable = false,
  allowMfaTypeSwitching = false,
  initialMfaType = null,
  phoneVerificationState,
}) => {
  const {
    phase,
    mfaType,
    currentMfaType,
    switchMfaType,
    error,
    verificationMethod,
    enrollmentValue,
    countryCode,
    consents,
    oobCode,
    otpSecret,
    secretAsQrCodeUri,
    challengedEnrollment,
    accessToken,

    resetError,
    onCloseModal,
    onSelectMfaType,
    advanceToChallengePhase,
    onConsentChange,
    onStartVerification,
    onChallengePassed,
    setVerificationMethod,
    setEnrollmentValue,
    setCountryCode,

    createCompletePhoneNumber,

    PHASES,
  } = useMfaEnrollment({
    restrictToMfaType,
    onSuccess,
    onClose,
    isUnskippable,
    allowMfaTypeSwitching,
    initialMfaType,
  });

  const { detectAndHandleAuthTokenExpirationError } = useStepUpAuthContext();

  if (!isOpen) {
    return null;
  }

  const activeMfaType = currentMfaType || mfaType || restrictToMfaType;
  const EnrollmentForm = EnrollmentForms[activeMfaType];

  const shouldShowForcedMfaContext =
    phoneVerificationState === PHONE_VERIFICATION_STATES.BANNER ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.MODAL ||
    phoneVerificationState === PHONE_VERIFICATION_STATES.UNSKIPPABLE;

  let dynamicModalTitle = modalTitle;
  if (activeMfaType === MANAGEMENT_TYPES.TOTP && isUnskippable) {
    dynamicModalTitle = messages.totpFallbackModalTitle;
  } else if (activeMfaType === MANAGEMENT_TYPES.PHONE && shouldShowForcedMfaContext) {
    dynamicModalTitle = messages.secureAccountModalHeader;
  } else if (activeMfaType === MANAGEMENT_TYPES.TOTP && shouldShowForcedMfaContext) {
    dynamicModalTitle = messages.totpFallbackModalTitle;
  }

  return (
    <ModalPortal hideCloseButton={isUnskippable} onClose={isUnskippable ? noop : onCloseModal}>
      <Modal size='medium'>
        <ModalHeader preventClose={isUnskippable}>
          <FormattedMessage {...dynamicModalTitle} />
        </ModalHeader>

        {phase === PHASES.selectType && showTypeSelection ? (
          <SelectEnrollmentType onCancel={onCloseModal} onContinue={onSelectMfaType} />
        ) : null}

        {phase === PHASES.start && EnrollmentForm ? (
          <EnrollmentForm
            advanceToChallengePhase={advanceToChallengePhase}
            consents={consents}
            countryCode={countryCode}
            currentMfaType={currentMfaType}
            enrollmentValue={enrollmentValue}
            error={error}
            isUnskippable={isUnskippable}
            onCancel={onCloseModal}
            onConsentChange={onConsentChange}
            onStartVerification={onStartVerification}
            phoneVerificationState={phoneVerificationState}
            qrCodeUri={secretAsQrCodeUri}
            resetError={resetError}
            secret={otpSecret}
            setCountryCode={setCountryCode}
            setEnrollmentValue={setEnrollmentValue}
            setVerificationMethod={setVerificationMethod}
            switchMfaType={switchMfaType}
            verificationMethod={verificationMethod}
          />
        ) : null}

        {phase === PHASES.challenge && (
          <ChallengePhaseModalContent
            challengedEnrollment={challengedEnrollment}
            consentDisclaimer={activeMfaType === MANAGEMENT_TYPES.PHONE ? messages.phoneConsentDisclaimer : undefined}
            consents={activeMfaType === MANAGEMENT_TYPES.PHONE ? consents : undefined}
            detectAndHandleAuthTokenExpirationError={detectAndHandleAuthTokenExpirationError}
            isMfaSettings
            isUnskippable={isUnskippable}
            mfaToken={accessToken}
            onCancel={onCloseModal}
            onChallengePassed={onChallengePassed}
            onResendCode={onStartVerification}
            oobCode={oobCode}
            userPhone={activeMfaType === MANAGEMENT_TYPES.PHONE ? createCompletePhoneNumber(countryCode.code, enrollmentValue) : undefined}
          />
        )}
      </Modal>
    </ModalPortal>
  );
};
